import { searchWorkPermitsForConnection, updatePermitParticipantsAsync } from "../apiClient";
import { showDangerDialog, showSuccessAlert } from "../common";
import { CodeStatus, Location, PermitType } from "../common/enums";
import { PermitFormProps } from "../types/forms/Permits/PermitFormProps";
import {
    UnderWaterWorkPermitFormState_default,
    UnderWaterWorkPermitFormState_populateErrorMessages,
    UnderWaterWorkPermitFormState_toRequest
} from "../types/forms/Permits/UnderWaterWorkPermitFormState";
import { UnderWaterWorkPermit_toFormState } from "../types/permits/UnderWaterWorkPermit";
import UpdateParticipantsRequest from "../types/requests/UpdateParticipantsRequest";
import usePortControlPermitForm from "./usePortControlPermitForm";
import useSearchDropdown from "./useSearchDropdown";

function useUnderWaterWorkPermitForm(props: PermitFormProps) {
    const baseFormManager = usePortControlPermitForm({
        ...props,
        defaultFormState: UnderWaterWorkPermitFormState_default,
        permitType: PermitType.UnderWaterWorkPermit,
        populateErrorMessages: UnderWaterWorkPermitFormState_populateErrorMessages,
        toFormState: UnderWaterWorkPermit_toFormState,
        toRequest: UnderWaterWorkPermitFormState_toRequest,
        clearErrorMessages: (formState) => UnderWaterWorkPermitFormState_populateErrorMessages(formState, {})
    });

    const { storedPermit } = baseFormManager;

    const energyHarbourPermitSearchHandler = useSearchDropdown({
        performSearch: async (input) => searchWorkPermitsForConnection(input, PermitType.EnergyHarbourPermit),
        searchErrorText: props.resources.get("SearchError") ?? "",
        toSearchResult: (wp) => {
            const positions = wp.positions
                .filter((pos) => pos.comment)
                .map((pos) => pos.comment)
                .join(", ");

            const searchResultText = `${wp.sequenceIdentifier}, ${wp.status}, ${positions}, ${wp.companyName}, ${wp.description}`;

            return { id: searchResultText, text: searchResultText, result: wp };
        },
        onSearchResultClick: (r) => {
            baseFormManager.setFormState((prevValue) => ({
                ...prevValue,
                energyHarbourSequenceIdentifier: {
                    ...prevValue.energyHarbourSequenceIdentifier,
                    value: r.result.sequenceIdentifier
                }
            }));
        }
    });

    async function updateParticipantsButtonClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Tried to update the participants with no stored permit.");
            return;
        }

        const request: UpdateParticipantsRequest = {
            projectLeaderIsActive: false,
            workLeaderIsActive: false,
            participants: baseFormManager.formState.persons.map((p) => ({
                isActive: p.isActive,
                name: p.name,
                phoneNumber: p.phoneNumber
            }))
        };

        const updateResult = await updatePermitParticipantsAsync(
            storedPermit.entityId,
            request,
            PermitType.UnderWaterWorkPermit
        );

        if (updateResult.failed) {
            if (!updateResult.error.errors) {
                showDangerDialog(
                    props.resources.get("SomethingWentWrong"),
                    props.resources.get("RefreshPageAndTryAgain")
                );
                return;
            }

            baseFormManager.handleValidationErrors(updateResult.error);
            return;
        }

        showSuccessAlert(props.resources.get("ParticipantsUpdated"));
        await baseFormManager.refreshStoredPermitAsync();
    }

    const showEnergyHarbourConditions = baseFormManager.formState.workLocation.value === Location.EnergyHarbour;
    const canManageParticipants = !storedPermit || storedPermit.canManageParticipants;

    const showUpdateParticipantsButton = !!storedPermit && !storedPermit.canUpdate && canManageParticipants;

    const showPrefillEnergyHarbourPermitButton = !!storedPermit && storedPermit.canPrefillEnergyHarbourPermit;

    return {
        ...baseFormManager,
        showEnergyHarbourConditions,
        showUpdateParticipantsButton,
        showPrefillEnergyHarbourPermitButton,
        energyHarbourPermitSearchHandler,
        canManageParticipants,
        updateParticipantsButtonClickHandlerAsync
    };
}

export default useUnderWaterWorkPermitForm;
